import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fileUrl, formatApiError, uploadImage } from "@/lib/api";

const newItem = (photo_path = null) => ({ id: crypto.randomUUID(), photo_path, name: "", notes: "", price: 0 });

function ItemCard({ item, index, onChange, onRemove, locked }) {
  const set = (k) => (e) => onChange({ ...item, [k]: e.target.value });
  return (
    <div className="rounded-2xl bg-white border border-[var(--border)] overflow-hidden flex flex-col sm:flex-row fade-up" data-testid={`item-card-${index}`}>
      <div className="sm:w-48 aspect-[4/3] sm:aspect-auto bg-[var(--surface-2)] shrink-0">
        {item.photo_path
          ? <img src={fileUrl(item.photo_path)} alt={item.name || "Oggetto"} className="h-full w-full object-cover" data-testid={`item-photo-${index}`} />
          : <div className="h-full w-full grid place-items-center text-[var(--muted)] text-xs">Nessuna foto</div>}
      </div>
      <div className="flex-1 p-4 space-y-3">
        <div className="flex gap-2">
          <Input placeholder="Nome oggetto (es. Credenza in noce)" disabled={locked} value={item.name} onChange={set("name")} data-testid={`item-name-input-${index}`} />
          {!locked && <button onClick={onRemove} className="btn-ghost text-red-600 shrink-0" data-testid={`item-remove-button-${index}`} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></button>}
        </div>
        <Textarea rows={3} placeholder="Appunti: condizioni, misure, difetti..." disabled={locked} value={item.notes} onChange={set("notes")} data-testid={`item-notes-input-${index}`} />
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">Valutazione €</span>
          <Input type="number" inputMode="decimal" min="0" step="0.5" disabled={locked} value={item.price} onChange={set("price")} className="w-32 font-mono" data-testid={`item-price-input-${index}`} />
        </div>
      </div>
    </div>
  );
}

export function ItemsEditor({ items, onChange, locked }) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const barRef = useRef(null);
  const count = useRef(items.length);
  useEffect(() => {
    if (items.length > count.current) barRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    count.current = items.length;
  }, [items.length]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(files.length);
    const added = [];
    for (const f of files) {
      try {
        added.push(newItem(await uploadImage(f)));
      } catch (err) {
        toast.error(formatApiError(err));
      }
      setUploading((n) => n - 1);
    }
    if (added.length) {
      onChange([...items, ...added]);
      toast.success(`${added.length} foto caricate. Ricorda di salvare.`);
    }
  };

  return (
    <div className="space-y-4">
      {items.length === 0 && <div className="rounded-2xl border border-dashed border-[var(--border)] p-10 text-center text-[var(--muted)]" data-testid="items-empty">Nessun oggetto. Scatta o carica le foto degli oggetti da valutare.</div>}
      {items.map((it, i) => (
        <ItemCard key={it.id} item={it} index={i} locked={locked}
          onChange={(v) => onChange(items.map((x) => (x.id === it.id ? v : x)))}
          onRemove={() => onChange(items.filter((x) => x.id !== it.id))} />
      ))}
      {uploading > 0 && <p className="flex items-center gap-2 text-sm text-[var(--muted)]" data-testid="upload-progress"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento di {uploading} foto...</p>}
      {!locked && (
        <div ref={barRef} className="flex flex-wrap gap-2 scroll-mb-24" data-testid="items-add-bar">
          <button onClick={() => cameraRef.current.click()} className="btn-primary h-11 px-4" data-testid="take-item-photo-button"><Camera className="h-5 w-5" /> Scatta foto</button>
          <button onClick={() => galleryRef.current.click()} className="btn-secondary h-11 px-4" data-testid="add-item-photo-button"><ImagePlus className="h-5 w-5" /> Carica dalla galleria</button>
          <button onClick={() => onChange([...items, newItem()])} className="btn-ghost h-11 px-4" data-testid="add-item-no-photo-button">+ Oggetto senza foto</button>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFiles} data-testid="item-camera-input" />
          <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="item-gallery-input" />
        </div>
      )}
    </div>
  );
}
